import { cleanPermalink } from '../../statistics/lib/top-videos';
import { format } from 'date-fns';

import { exactRu } from '../../statistics/lib/format-metrics-ru';
import type { SeedingGroup } from './group-seeding';
import type { SeedingComment } from './pick-comments';
import type { CommentImages } from './comment-images';
import { drawCommentCards, type CommentCardImage } from './render-comment-card';
import { renderCovers, type CoverImage } from './render-cover';

/**
 * Отчёт «Отработано» в Word.
 *
 * Формат снят с файла «Еженедельный отчет с комментариями 23.09.2026.docx»,
 * который собирали руками: шапка, период, «Топ N видео:», и на каждый посев —
 * тема, все ссылки списком и четыре строки цифр, просуммированных по копиям.
 *
 * Оформление образца воспроизводится как есть, включая тему белым по чёрному:
 * так размечены все пять посевов файла, значит это принятый вид, а не случайный
 * след вставки.
 *
 * Комментарии в образце — скриншоты из приложения, сделанные руками. Здесь они
 * рисуются картинкой в тот же вид (см. render-comment-card) и вставляются как
 * изображения — блоком без отступа, ровно как в образце. Если нарисовать не
 * вышло, комментарий всё равно попадёт в отчёт текстовой карточкой: пустое
 * место в блоке хуже, чем строка без картинки.
 */

// Образец — выгрузка из Google Docs: Times New Roman 11 pt, межстрочный 1,15.
const FONT = 'Times New Roman';
const FONT_SIZE = 22;
const LINE = 276;
// Шапка в образце набрана шрифтом с галочкой в наборе: Times её не рисует.
const HEADING_FONT = 'Arial Unicode MS';
const HEADING = 'ОТРАБОТАНО Instagram ✅';
// Ссылки и цифры сдвинуты на два дюйма, тема — по левому краю.
const INDENT = 2880;
// Цвет ссылок из образца — гугловский, не вордовский.
const LINK_COLOR = '1155cc';
const COMMENTS_HEADING = 'Топовые комментарии под роликом:';

/**
 * Карточка комментария. Цвета и порядок строк — из приложения Instagram
 * (ровно то, что видно на скриншотах образца), шрифт тоже его: карточка —
 * цитата чужого интерфейса, и набирать её основным Times было бы враньём.
 */
const CARD = {
  font: 'Arial',
  width: 5200,
  fill: 'fafafa',
  divider: 'efefef',
  name: '262626',
  meta: '8e8e8e',
  nameSize: 20,
  textSize: 22,
  metaSize: 18,
};

export type SeedingDocParams = {
  groups: SeedingGroup[];
  /** Период в шапке; нет — строка не печатается. */
  period: { from: Date; to: Date } | null;
  /**
   * Нарисованные карточки комментариев по id комментария. Рисует их браузер,
   * поэтому они приходят снаружи: тест собирает документ без canvas.
   */
  cards?: Map<string, CommentCardImage>;
  /** Обложки блоков по адресу обложки (group.cover) — по той же причине снаружи. */
  covers?: Map<string, CoverImage>;
};

/** «16.09 - 22.09.2026» — год только в конце, как в образце. */
export function formatPeriod(period: { from: Date; to: Date }): string {
  return `${format(period.from, 'dd.MM')} - ${format(period.to, 'dd.MM.yyyy')}`;
}

/**
 * Сам документ, без скачивания — отдельно, чтобы тест мог собрать его и
 * проверить, что попало в файл.
 */
export async function buildSeedingDocument(params: SeedingDocParams) {
  if (params.groups.length === 0) throw new Error('Нет данных для экспорта');

  const {
    AlignmentType,
    BorderStyle,
    Document,
    ExternalHyperlink,
    ImageRun,
    Paragraph,
    ShadingType,
    Table,
    TableCell,
    TableLayoutType,
    TableRow,
    TextRun,
    WidthType,
  } = await import('docx');

  // Шрифт задаётся на каждом фрагменте, а не только в умолчаниях документа:
  // Pages и просмотр macOS умолчания игнорируют и показывают свой шрифт.
  const text = (
    value: string,
    options: { bold?: boolean; font?: string; color?: string; highlight?: 'black'; underline?: object } = {},
  ) => new TextRun({ text: value, font: options.font ?? FONT, size: FONT_SIZE, ...options });

  const line = (value: string, indent = 0) =>
    new Paragraph({ indent: indent ? { left: indent } : undefined, children: [text(value)] });
  const blank = () => new Paragraph({ children: [] });

  const linkLine = (url: string) =>
    new Paragraph({
      indent: { left: INDENT },
      children: [
        new ExternalHyperlink({
          link: cleanPermalink(url),
          children: [text(cleanPermalink(url), { color: LINK_COLOR, underline: { type: 'single' } })],
        }),
        // Хвост обычным текстом: без него просмотр macOS теряет отступ у
        // абзаца, в котором кроме ссылки ничего нет.
        text(' '),
      ],
    });

  const cardText = (value: string, color: string, size: number, bold = false) =>
    new TextRun({ text: value, font: CARD.font, size, color, bold });

  /**
   * Комментарии одной таблицей: строка на комментарий, тонкая линия между —
   * так же, как они идут в ленте комментариев приложения.
   */
  const commentCards = (comments: SeedingComment[]) => {
    const none = { style: BorderStyle.NONE, size: 0, color: 'ffffff' };
    const divider = { style: BorderStyle.SINGLE, size: 4, color: CARD.divider };
    return new Table({
      width: { size: CARD.width, type: WidthType.DXA },
      columnWidths: [CARD.width],
      layout: TableLayoutType.FIXED,
      borders: {
        top: none,
        bottom: none,
        left: none,
        right: none,
        insideHorizontal: divider,
        insideVertical: none,
      },
      rows: comments.map(
        (c) =>
          new TableRow({
            // Комментарий не рвётся между страницами: половина карточки
            // на обороте читается как обрывок.
            cantSplit: true,
            children: [
              new TableCell({
                width: { size: CARD.width, type: WidthType.DXA },
                shading: { fill: CARD.fill, type: ShadingType.CLEAR, color: 'auto' },
                margins: { top: 90, bottom: 90, left: 110, right: 110 },
                children: [
                  new Paragraph({
                    children: [
                      cardText(c.username, CARD.name, CARD.nameSize, true),
                      ...(c.date ? [cardText(`   ${c.date}`, CARD.meta, CARD.metaSize)] : []),
                    ],
                  }),
                  new Paragraph({
                    spacing: { before: 40 },
                    children: [cardText(c.text, CARD.name, CARD.textSize)],
                  }),
                  new Paragraph({
                    spacing: { before: 60 },
                    children: [
                      cardText(
                        `Отметки «Нравится»: ${exactRu(c.likeCount)}`,
                        CARD.meta,
                        CARD.metaSize,
                      ),
                    ],
                  }),
                ],
              }),
            ],
          }),
      ),
    });
  };

  /** Комментарий картинкой: как его приносили руками в образце. */
  const cardImage = (card: CommentCardImage) =>
    new Paragraph({
      spacing: { before: 40 },
      children: [
        new ImageRun({
          data: card.dataUrl.split(',')[1],
          type: 'png',
          transformation: { width: card.width, height: card.height },
        }),
      ],
    });

  /**
   * Обложка ролика под ссылками, по левому краю, как кадр в образце. Нет
   * картинки — нет и абзаца: пустое место под обложку никому не нужно.
   */
  const coverBlock = (group: SeedingGroup) => {
    const cover = group.cover ? params.covers?.get(group.cover) : undefined;
    if (!cover) return [];
    return [
      new Paragraph({
        spacing: { before: 120 },
        keepNext: true,
        children: [
          new ImageRun({
            data: cover.dataUrl.split(',')[1],
            type: 'jpg',
            transformation: { width: cover.width, height: cover.height },
          }),
        ],
      }),
    ];
  };

  const commentsBlock = (comments: SeedingComment[] | undefined) => {
    if (!comments || comments.length === 0) return [];
    const head = [
      blank(),
      new Paragraph({ keepNext: true, children: [text(COMMENTS_HEADING, { bold: true })] }),
    ];
    const cards = params.cards;
    // Нарисовано всё — блок идёт картинками. Нарисовано не всё (canvas не
    // поднялся) — старая текстовая таблица целиком, чтобы вид не был рваным.
    const drawn = cards ? comments.map((c) => cards.get(c.id)) : [];
    if (cards && drawn.every(Boolean)) {
      return [...head, ...drawn.map((card) => cardImage(card as CommentCardImage))];
    }
    return [...head, commentCards(comments)];
  };

  const metricLines = (group: SeedingGroup) => {
    const { views, likes, comments, reposts } = group.totals;
    const rows: Array<[string, number]> = [
      ['Просмотры', views],
      ['Лайки', likes],
      ['Комментарии', comments],
    ];
    // Репостов может не быть вовсе: Instagram отдаёт их расчётом, и на части
    // публикаций расчёт не сходится. «Репосты - 0» читались бы как провал.
    if (reposts !== null) rows.push(['Репосты', reposts]);
    return rows.map(([label, value]) => line(`${label} - ${exactRu(value)}`, INDENT));
  };

  const groupBlock = (group: SeedingGroup) => [
    new Paragraph({
      keepNext: true,
      children: [
        // Тема без подписи — пустой абзац: место под неё оператор заполнит сам.
        text(group.headline, group.headline ? { color: 'ffffff', highlight: 'black' } : {}),
      ],
    }),
    ...group.items.map((item) => linkLine(item.url)),
    ...coverBlock(group),
    ...commentsBlock(group.comments),
    blank(),
    ...metricLines(group),
    blank(),
  ];

  return new Document({
    styles: {
      default: {
        document: {
          run: { font: FONT, size: FONT_SIZE },
          paragraph: { spacing: { line: LINE, lineRule: 'auto', after: 0 } },
        },
      },
    },
    sections: [
      {
        children: [
          new Paragraph({
            alignment: AlignmentType.LEFT,
            children: [text(HEADING, { font: HEADING_FONT })],
          }),
          ...(params.period ? [line(formatPeriod(params.period))] : []),
          blank(),
          line(`Топ ${params.groups.length} видео:`),
          blank(),
          ...params.groups.flatMap(groupBlock),
        ],
      },
    ],
  });
}

/**
 * Карточки всех комментариев отчёта. Сбой на одном не роняет выгрузку: этот
 * комментарий просто уйдёт текстом.
 */
export function drawCards(
  groups: SeedingGroup[],
  images: Map<string, CommentImages> = new Map(),
): Map<string, CommentCardImage> {
  return drawCommentCards(groups.flatMap((group) => group.comments ?? []), images);
}

export async function generateSeedingDocx(params: SeedingDocParams): Promise<void> {
  const doc = await buildSeedingDocument({
    ...params,
    cards: params.cards ?? drawCards(params.groups),
    covers: params.covers ?? (await renderCovers(params.groups)),
  });
  const { Packer } = await import('docx');
  // Имя как у образца: «Еженедельный отчет с комментариями 23.09.2026.docx».
  const filename = `Еженедельный отчет с комментариями ${format(new Date(), 'dd.MM.yyyy')}.docx`;
  const blob = await Packer.toBlob(doc);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
